<template>
  <div class="language-dropdown-wrapper">
    <VaDropdown v-model="isShown" :offset="[9, 0]" class="language-dropdown" stick-to-edges>
      <template #anchor>
        <VaButton preset="secondary" color="textPrimary">
          <span class="language-dropdown__anchor min-w-max">
            <VaIcon name="language" class="mr-1" />
            {{ currentLanguageDisplay }}
          </span>
        </VaButton>
      </template>
      <VaDropdownContent class="language-dropdown__content px-0 py-2 w-40" :style="{ '--hover-color': hoverColor }">
        <VaList>
          <VaListItem
            v-for="option in options"
            :key="option"
            class="menu-item px-4 text-base cursor-pointer h-8"
            @click="selectLanguage(option)"
          >
            {{ option }}
          </VaListItem>
        </VaList>
      </VaDropdownContent>
    </VaDropdown>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useColors } from 'vuestic-ui'

const { colors, setHSLAColor } = useColors()
const hoverColor = computed(() => setHSLAColor(colors.focus, { a: 0.1 }))

const { locale } = useI18n()

const isShown = ref(false)

// Language mappings
const languages = {
  english: 'English',
  russian: 'Русский',
}

const languageCodes = {
  gb: languages.english,
  ru: languages.russian,
}

const languageName = Object.fromEntries(Object.entries(languageCodes).map(([key, value]) => [value, key]))

const options = Object.values(languageCodes)

// Computed property to get the display name of the current language
const currentLanguageDisplay = computed(() => {
  return languageCodes[locale.value] || languages.english
})

// Function to select a language
const selectLanguage = (language: string) => {
  const code = languageName[language]
  if (code) {
    locale.value = code
    // Save the selected language to localStorage
    localStorage.setItem('selectedLanguage', code)
    isShown.value = false
  }
}

// Load the saved language from localStorage on component mount
onMounted(() => {
  const savedLanguage = localStorage.getItem('selectedLanguage')
  if (savedLanguage && Object.keys(languageCodes).includes(savedLanguage)) {
    locale.value = savedLanguage
  }
})

// Watch for changes to the locale and save to localStorage
watch(locale, (newLocale) => {
  localStorage.setItem('selectedLanguage', newLocale)
})
</script>

<style lang="scss">
.language-dropdown {
  cursor: pointer;

  &__content {
    .menu-item:hover {
      background: var(--hover-color);
    }
  }

  &__anchor {
    display: inline-flex;
    align-items: center;
  }
}
</style>
